import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, NgClass } from '@angular/common';
import { RouterModule } from '@angular/router';import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCartShopping,
  faCheck,
  faFlag,
  faLayerGroup,
  faLink,
  faPlus,
  faRotateRight,
  faTrash,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';

import { Context } from '../../models/context';
import { Product } from '../../models/product';
import {
  FrequentlyBoughtItem,
  LocalShoppingListItem,
  RecentTripItem,
  ShoppingList,
  ShoppingListWithItems,
  ShoppingTrip,
  ShoppingTripItem,
} from '../../models/shopping-list';import { ContextService } from '../../services/context/context-signal.service';
import { PageHeaderService } from '../../services/page-header/page-header.service';
import { ProductService } from '../../services/product/product.service';
import { ShoppingService } from '../../services/shopping/shopping.service';
import { ShoppingSyncService } from '../../services/shopping/shopping-sync.service';
import { AppEventsService } from '../../services/app-events/app-events.service';
import { provideViewContext } from '../../services/view-context/provide-view-context';
import { ThemeService } from '../../services/theme.service';
import { ActiveIconPackService } from '../../services/active-icon-pack.service';
import { AppLogger, AppLoggerService } from '../../core/logging';
import { UiButtonComponent, UiCollapseComponent, UiInputComponent, UiToastService } from '../../shared/ui';
import { AutoAnimateDirective } from '../../directives/auto-animate.directive';
import { ProductNamePipe } from '../../pipes/product-name.pipe';
import { ShoppingListSwitcherComponent } from '../../components/shopping-list-switcher/shopping-list-switcher.component';

/** Source of a quick-add autosuggestion, used to colorize the entry. */
export type QuickAddSuggestionSource = 'active' | 'bought' | 'past';

/** A single de-duplicated entry in the quick-add autosuggest dropdown. */
export interface QuickAddSuggestion {
  /** Stable de-dupe key (product_key when known, else normalized title). */
  key: string;
  title: string;
  product_key?: string | null;
  product_id?: number | null;
  /** Set for entries that already exist on the active list. */
  client_id?: string;
  source: QuickAddSuggestionSource;
}

@Component({
  selector: 'app-shopping',
  standalone: true,
  imports: [
    RouterModule,
    FontAwesomeModule,
    UiButtonComponent,
    UiCollapseComponent,
    UiInputComponent,
    AutoAnimateDirective,
    ProductNamePipe,
    DatePipe,
    NgClass,
    ShoppingListSwitcherComponent,
  ],
  templateUrl: './shopping.component.html',
  styleUrl: './shopping.component.scss',
})
export class ShoppingComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly contextService = inject(ContextService);
  private readonly pageHeaderService = inject(PageHeaderService);
  private readonly shoppingService = inject(ShoppingService);
  private readonly sync = inject(ShoppingSyncService);
  private readonly appEvents = inject(AppEventsService);
  private readonly productService = inject(ProductService);
  private readonly themeService = inject(ThemeService);
  private readonly activeIconPack = inject(ActiveIconPackService);
  private readonly toast = inject(UiToastService);
  private readonly appLogger = inject(AppLoggerService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly context: Signal<Context> = this.contextService.context;
  private readonly log: AppLogger;

  /** Per-item icon load tracking (keyed by `client_id`). */
  iconLoading: Record<string, boolean> = {};
  iconFailed: Record<string, boolean> = {};

  // Icon loading state for suggestion-panel product thumbnails (keyed by product.id)
  prodIconLoading: Record<number, boolean> = {};
  prodIconFailed: Record<number, boolean> = {};

  // ── Icons ─────────────────────────────────────────────────────────────────
  faCartShopping = faCartShopping;
  faPlus = faPlus;
  faCheck = faCheck;
  faTrash = faTrash;
  faFlag = faFlag;
  faLayerGroup = faLayerGroup;
  faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  faLink = faLink;
  faRotateRight = faRotateRight;
  faTriangleExclamation = faTriangleExclamation;

  // ── Lists state ───────────────────────────────────────────────────────────
  lists: ShoppingList[] = [];
  /** Active list metadata. Items themselves live in the sync service. */
  activeList: ShoppingList | null = null;
  loadingLists = false;
  loadingItems = false;

  /** Items signal exposed from the sync service. */
  readonly items = this.sync.items;

  // ── Trip state ────────────────────────────────────────────────────────────
  activeTrip: ShoppingTrip | null = null;
  finishingTrip = false;
  completedTrips: ShoppingTrip[] = [];
  tripDetailsMap: Record<number, ShoppingTrip> = {};
  tripDetailsLoading: Record<number, boolean> = {};
  tripCollapseOpen: Record<number, boolean> = {};

  // ── Quick-add state ───────────────────────────────────────────────────────
  quickAddText = '';
  /** Whether the quick-add input currently has focus (drives the dropdown). */
  quickAddFocused = false;
  /** Items bought across recent trips, used to seed past-item suggestions. */
  recentTripItems: RecentTripItem[] = [];
  private blurTimer: ReturnType<typeof setTimeout> | null = null;

  // ── Suggestion panels ─────────────────────────────────────────────────────
  outOfStockProducts: Product[] = [];
  lowStockProducts: Product[] = [];
  loadingOutOfStock = false;
  loadingLowStock = false;
  /** Purchase-history suggestions (incl. free-text items with no product). */
  frequentItems: FrequentlyBoughtItem[] = [];
  loadingFrequent = false;

  // ── Export providers ──────────────────────────────────────────────────────
  exportLinks: Record<string, string> = {};
  loadingExportLinks = false;

  // ── Toolbar templates ─────────────────────────────────────────────────────
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @ViewChild('toolbarSearch') toolbarSearchTemplate!: TemplateRef<any>;

  constructor() {
    this.log = this.appLogger.createForClass(ShoppingComponent);

    provideViewContext('shopping_list', () =>
      this.activeList ? [{type: 'shopping_list', id: this.activeList.id, label: this.activeList.name}] : []
    );

    // Refresh immediately when the dialog assistant adds an item, rather than
    // waiting for the next polling tick.
    this.appEvents.shoppingListChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const hid = this.context()?.household?.id;
        const listId = this.activeList?.id;
        if (hid && listId) this.loadListItems(hid, listId);
      });

    // Re-load whenever the household changes
    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;
      const hid = c.household?.id;
      if (hid) {
        this.initLists(hid);
        this.loadSuggestions(hid);
      }
    });

    // The sync service refetches items whenever the server signals a change
    // over the live SSE stream. Hook into that to refresh trips so a trip
    // created by another client (e.g. iOS) shows up here without a manual
    // page refresh.
    this.sync.polled$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const hid = this.householdId;
        const listId = this.activeList?.id;
        if (hid && listId) this.refreshTrips(hid, listId);
      });
  }

  ngOnInit(): void {
    this.pageHeaderService.setPageTitle('Shopping');
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.toolbarSearchTemplate) {
        this.pageHeaderService.setSearch(this.toolbarSearchTemplate);
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearAll();
    this.sync.clearActiveList();
    if (this.blurTimer) { clearTimeout(this.blurTimer); this.blurTimer = null; }
  }

  // ── List bootstrapping ────────────────────────────────────────────────────

  private initLists(householdId: number): void {
    this.loadingLists = true;
    this.shoppingService.getLists(householdId).subscribe({
      next: lists => {
        this.lists = lists;
        this.loadingLists = false;
        if (lists.length === 0) {
          this.createDefaultList(householdId);
        } else {
          const active = lists.find(l => l.is_active) ?? lists[0];
          this.loadListItems(householdId, active.id);
        }
      },
      error: err => {
        this.log.error('Failed to load shopping lists', err);
        this.loadingLists = false;
        this.toast.error('Could not load shopping lists.');
      },
    });
  }

  private createDefaultList(householdId: number): void {
    this.loadingLists = true;
    this.shoppingService.createList(householdId, { name: 'Shopping' }).subscribe({
      next: list => {
        this.lists = [list];
        this.loadingLists = false;
        this.loadListItems(householdId, list.id);
      },
      error: err => {
        this.log.error('Failed to create default shopping list', err);
        this.loadingLists = false;
        this.toast.error('Could not create shopping list.');
      },
    });
  }

  private loadListItems(householdId: number, listId: number): void {
    this.loadingItems = true;
    this.shoppingService.getList(listId).subscribe({
      next: (list: ShoppingListWithItems) => {
        this.activeList = {
          id: list.id,
          household_id: list.household_id,
          name: list.name,
          is_active: list.is_active,
          created_at: list.created_at,
          updated_at: list.updated_at,
        };
        this.lists = this.lists.map(l => ({ ...l, is_active: l.id === list.id }));
        this.sync.setActiveList(householdId, listId, list.items ?? []);
        this.loadingItems = false;
        this.loadActiveTrip(householdId, listId);
      },
      error: err => {
        this.log.error('Failed to load shopping list items', err, { listId });
        this.loadingItems = false;
        this.toast.error('Could not load list items.');
      },
    });
  }

  private loadActiveTrip(householdId: number, listId: number): void {
    this.shoppingService.getTrips(householdId, listId).subscribe({
      next: trips => {
        this.activeTrip = trips.find(t => !t.completed_at) ?? null;
        this.completedTrips = trips.filter(t => !!t.completed_at).slice(0, 5);
        if (this.completedTrips.length > 0) {
          const firstId = this.completedTrips[0].id;
          if (this.tripCollapseOpen[firstId] === undefined) {
            this.tripCollapseOpen[firstId] = true;
            this.onTripOpen(this.completedTrips[0]);
          }
        }
      },
      error: err => {
        this.log.warn?.('Could not load trips', err);
        this.activeTrip = null;
        this.completedTrips = [];
      },
    });
  }

  /**
   * Lightweight trip refresh used by the polling tick. Only updates the
   * trip arrays — does not auto-expand collapses or touch user UI state.
   */
  private refreshTrips(householdId: number, listId: number): void {
    this.shoppingService.getTrips(householdId, listId).subscribe({
      next: trips => {
        this.activeTrip = trips.find(t => !t.completed_at) ?? null;
        this.completedTrips = trips.filter(t => !!t.completed_at).slice(0, 5);
      },
      error: () => { /* non-fatal — polling will try again */ },
    });
  }

  // ── Suggestions ───────────────────────────────────────────────────────────

  private loadSuggestions(householdId: number): void {
    this.loadingOutOfStock = true;
    this.productService.getAllProducts(householdId, { stockStatus: 'out' }).subscribe({
      next: prods => { this.outOfStockProducts = prods ?? []; },
      error: () => { this.outOfStockProducts = []; },
      complete: () => { this.loadingOutOfStock = false; },
    });

    this.loadingLowStock = true;
    this.productService.getAllProducts(householdId, { stockStatus: 'low' }).subscribe({
      next: prods => { this.lowStockProducts = prods ?? []; },
      error: () => { this.lowStockProducts = []; },
      complete: () => { this.loadingLowStock = false; },
    });

    this.loadingExportLinks = true;
    this.shoppingService.restockLinks(householdId).subscribe({
      next: data => { this.exportLinks = (data as unknown as Record<string, string>) ?? {}; },
      error: () => { this.exportLinks = {}; },
      complete: () => { this.loadingExportLinks = false; },
    });

    // Recent-trip items power the "past" quick-add autosuggestions.
    this.shoppingService.getRecentTripItems(householdId, 5).subscribe({
      next: items => { this.recentTripItems = items ?? []; },
      error: () => { this.recentTripItems = []; },
    });

    this.loadingFrequent = true;
    this.shoppingService.getFrequentlyBoughtItems(householdId, 12).subscribe({
      next: items => { this.frequentItems = items ?? []; },
      error: () => { this.frequentItems = []; },
      complete: () => { this.loadingFrequent = false; },
    });
  }

  get exportLinksArray(): { key: string; value: string }[] {
    return Object.entries(this.exportLinks).map(([key, value]) => ({ key, value }));
  }

  providerLogoUrl(provider: string): string | null {
    if (provider === 'getbring') return 'assets/img/shopping-list-provider-logo/getbring.png';
    return null;
  }

  providerLabel(provider: string): string {
    if (provider === 'getbring') return 'Bring!';
    return provider;
  }

  resolveProviderAction(provider: string, link: string): string {
    if (provider === 'getbring') {
      return `https://api.getbring.com/rest/bringrecipes/deeplink?url=${link}&source=web&baseQuantity=1&requestedQuantity=1`;
    }
    return link;
  }

  productThumbUrl(p: Product): string {
    return p.primary_thumb_image?.url
      || p.image_thumb_url
      || `https://placehold.co/256x256/EEE/31343C?font=noto-sans&text=${encodeURIComponent(p.name ?? '?')}`;
  }

  // ── Suggestion-panel clicks ───────────────────────────────────────────────
  // Click → enqueue an item with the `{ client_id, product_id }` payload.
  // The card appears optimistically; the server response populates title,
  // subtitle and product_key on reconciliation.

  addProductToList(product: Product): void {
    if (!this.activeList || product.id == null) return;
    if (this.isProductAdded(product)) return;
    const name = this.productNamePipe.transform(product);
    const qty = product.quantity_as_text;
    this.sync.enqueueProduct(product.id, name, qty ?? null);
  }

  addAllToList(products: Product[], _section: 'out' | 'low'): void {
    void _section;
    for (const p of products) {
      if (p.id == null) continue;
      if (this.isProductAdded(p)) continue;
      this.addProductToList(p);
    }
  }

  /** True if the suggestion's product is already represented in the list. */
  isProductAdded(product: Product): boolean {
    if (product.id == null) return false;
    const pid = product.id;
    return this.items().some(i => i.product_id === pid);
  }

  /**
   * Add a "Frequently Bought" suggestion. Product-linked items go through the
   * product path (server copies name/kind); everything else — including
   * free-text items with no product — is added as a structured item so its
   * title/subtitle/kind are preserved verbatim (no re-parse).
   *
   * The item is dropped from the section optimistically; the backend also
   * excludes it from the next fetch once it's pending on the list.
   */
  addFrequentToList(item: FrequentlyBoughtItem): void {
    if (!this.activeList) return;
    if (item.product_id != null) {
      this.sync.enqueueProduct(item.product_id, item.title, item.subtitle ?? null);
    } else {
      this.sync.enqueueStructured(item.title, item.subtitle ?? null, item.product_key ?? null);
    }
    this.frequentItems = this.frequentItems.filter(f => f !== item);
  }

  /** Kind icon for a frequently-bought suggestion, or null when it has none. */
  frequentIconUrl(item: FrequentlyBoughtItem): string | null {
    return item.product_key ? this.productIconUrl(item.product_key) : null;
  }

  // "Add all" runs synchronously against the queue, so there's no async
  // loading state to expose. Kept as constants for the template bindings.
  readonly addingAllOutOfStock = false;
  readonly addingAllLowStock = false;

  private readonly productNamePipe = new ProductNamePipe();

  // ── Trip details ──────────────────────────────────────────────────────────

  onTripOpen(trip: ShoppingTrip): void {
    const hid = this.householdId;
    const listId = this.activeList?.id;
    if (!hid || !listId) return;
    if (this.tripDetailsMap[trip.id] || this.tripDetailsLoading[trip.id]) return;

    this.tripDetailsLoading[trip.id] = true;
    this.shoppingService.getTrip(trip.id).subscribe({
      next: detail => {
        this.tripDetailsMap = { ...this.tripDetailsMap, [trip.id]: detail };
        this.tripDetailsLoading[trip.id] = false;
      },
      error: err => {
        this.log.error('Failed to load trip details', err, { tripId: trip.id });
        this.tripDetailsLoading[trip.id] = false;
        this.toast.error('Could not load trip details.');
      },
    });
  }

  onTripCollapseChange(trip: ShoppingTrip, isOpen: boolean): void {
    this.tripCollapseOpen[trip.id] = isOpen;
    if (isOpen) this.onTripOpen(trip);
  }

  isTripOpen(trip: ShoppingTrip): boolean {
    return !!this.tripCollapseOpen[trip.id];
  }

  /** Re-add a past-trip item by re-queuing its title as raw input. */
  reAddTripItem(tripItem: ShoppingTripItem): void {
    if (!this.activeList) return;
    const raw = tripItem.subtitle
      ? `${tripItem.title} ${tripItem.subtitle}`
      : tripItem.title;
    this.sync.enqueueRawInput(raw);
  }

  // ── Subtitle inline-edit ──────────────────────────────────────────────────
  subtitleDraft: Record<string, string> = {};

  onSubtitleFocus(item: LocalShoppingListItem): void {
    this.subtitleDraft[item.client_id] = item.subtitle ?? '';
  }

  onSubtitleBlur(item: LocalShoppingListItem, event: FocusEvent): void {
    const newValue = (event.target as HTMLInputElement).value.trim();
    const original = item.subtitle ?? '';
    if (newValue !== original) {
      this.sync.updateSubtitle(item.client_id, newValue || null);
    }
    delete this.subtitleDraft[item.client_id];
  }

  onSubtitleEscape(item: LocalShoppingListItem, event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    input.value = item.subtitle ?? '';
    delete this.subtitleDraft[item.client_id];
    input.blur();
    event.stopPropagation();
  }

  // ── List-switcher events ──────────────────────────────────────────────────

  switchList(list: ShoppingList): void {
    const hid = this.householdId;
    if (!hid || list.id === this.activeList?.id) return;
    this.sync.clearActiveList();
    this.shoppingService.activateList(hid, list.id).subscribe({
      next: () => this.loadListItems(hid, list.id),
      error: err => {
        this.log.error('Failed to activate list', err, { listId: list.id });
        this.toast.error('Could not switch list.');
      },
    });
  }

  onListCreated(list: ShoppingList): void {
    const hid = this.householdId;
    if (!hid) return;
    this.lists = [...this.lists, list];
    this.sync.clearActiveList();
    this.shoppingService.activateList(hid, list.id).subscribe({
      next: () => this.loadListItems(hid, list.id),
      error: () => this.loadListItems(hid, list.id),
    });
  }

  onListRenamed(updated: ShoppingList): void {
    this.lists = this.lists.map(l => l.id === updated.id ? { ...l, name: updated.name } : l);
    if (this.activeList?.id === updated.id) {
      this.activeList = { ...this.activeList, name: updated.name };
    }
  }

  onListDeleted(deletedId: number): void {
    const hid = this.householdId;
    if (!hid) return;
    const remaining = this.lists.filter(l => l.id !== deletedId);
    this.lists = remaining;

    if (this.activeList?.id === deletedId) {
      this.sync.clearActiveList();
      if (remaining.length > 0) {
        const next = remaining[0];
        this.shoppingService.activateList(hid, next.id).subscribe({
          next: () => this.loadListItems(hid, next.id),
          error: () => this.loadListItems(hid, next.id),
        });
      } else {
        this.createDefaultList(hid);
      }
    }
  }

  // ── Quick-add ─────────────────────────────────────────────────────────────

  onQuickAddInput(value: string): void {
    this.quickAddText = value;
    // Typing implies focus. After picking a suggestion we hide the dropdown
    // without blurring the input, so the `focused` event won't fire again —
    // re-arm it here so further typing re-opens the suggestions.
    if (this.blurTimer) { clearTimeout(this.blurTimer); this.blurTimer = null; }
    this.quickAddFocused = true;
  }

  clearQuickAdd(): void {
    this.quickAddText = '';
  }

  quickAdd(): void {
    if (!this.activeList) return;
    const rawInput = this.quickAddText.trim();
    if (!rawInput) return;
    this.clearQuickAdd();
    this.sync.enqueueRawInput(rawInput);
  }

  // ── Quick-add autosuggest ─────────────────────────────────────────────────

  onQuickAddFocus(): void {
    if (this.blurTimer) { clearTimeout(this.blurTimer); this.blurTimer = null; }
    this.quickAddFocused = true;
  }

  onQuickAddBlur(): void {
    // Delay so a click on a suggestion (which steals focus) still registers.
    this.blurTimer = setTimeout(() => { this.quickAddFocused = false; }, 150);
  }

  /** Normalized de-dupe key: product_key when known, else lower-cased title. */
  private suggestionKey(productKey: string | null | undefined, title: string): string {
    if (productKey) return `p:${productKey}`;
    return `t:${title.trim().toLowerCase()}`;
  }

  /**
   * Unified, de-duplicated suggestion list. Active (pending) list items take
   * priority over bought (done) list items, which take priority over items
   * pulled from recent trips. Filtered by the current input text.
   */
  get quickAddSuggestions(): QuickAddSuggestion[] {
    const byKey = new Map<string, QuickAddSuggestion>();
    const add = (s: QuickAddSuggestion): void => {
      if (!byKey.has(s.key)) byKey.set(s.key, s);
    };

    // 1) Items already on the list, pending → "active".
    for (const i of this.items()) {
      if (i.state !== 'pending') continue;
      add({
        key: this.suggestionKey(i.product_key, i.title),
        title: i.title,
        product_key: i.product_key ?? null,
        product_id: i.product_id ?? null,
        client_id: i.client_id,
        source: 'active',
      });
    }
    // 2) Items already on the list, done → "bought".
    for (const i of this.items()) {
      if (i.state !== 'done') continue;
      add({
        key: this.suggestionKey(i.product_key, i.title),
        title: i.title,
        product_key: i.product_key ?? null,
        product_id: i.product_id ?? null,
        client_id: i.client_id,
        source: 'bought',
      });
    }
    // 3) Items from recent trips → "past".
    for (const r of this.recentTripItems) {
      add({
        key: this.suggestionKey(r.product_key, r.title),
        title: r.title,
        product_key: r.product_key ?? null,
        product_id: r.product_id ?? null,
        source: 'past',
      });
    }

    let result = [...byKey.values()];
    const q = this.quickAddText.trim().toLowerCase();
    if (q) result = result.filter(s => s.title.toLowerCase().includes(q));
    return result;
  }

  get showQuickAddSuggestions(): boolean {
    return this.quickAddFocused && this.quickAddSuggestions.length > 0;
  }

  suggestionLabel(source: QuickAddSuggestionSource): string {
    switch (source) {
      case 'active': return 'On list';
      case 'bought': return 'Bought';
      case 'past': return 'Last trip';
    }
  }

  suggestionBadgeClass(source: QuickAddSuggestionSource): string {
    switch (source) {
      case 'active': return 'badge-info';
      case 'bought': return 'badge-success';
      case 'past': return 'badge-ghost';
    }
  }

  /**
   * Act on a chosen suggestion. Subtitles are intentionally ignored — only
   * the product (when known) or the bare title is added.
   *
   *   - active : already on the list (pending) → no-op, just dismiss.
   *   - bought : already on the list (done) → flip it back to pending.
   *   - past   : add a fresh item (by product when known, else raw title).
   */
  selectSuggestion(s: QuickAddSuggestion): void {
    this.clearQuickAdd();
    this.quickAddFocused = false;

    if (s.source === 'active') return;

    if (s.source === 'bought' && s.client_id) {
      void this.sync.toggleState(s.client_id);
      return;
    }

    if (!this.activeList) return;
    if (s.product_id != null) {
      this.sync.enqueueProduct(s.product_id, s.title, null);
    } else {
      this.sync.enqueueRawInput(s.title);
    }
  }

  // ── Item actions ──────────────────────────────────────────────────────────

  toggleItem(item: LocalShoppingListItem): void {
    // While the item is still pending sync, only flip locally; the next
    // POST response (or polling reconciliation) will resolve it.
    if (item.id == null) {
      void this.sync.toggleState(item.client_id);
      return;
    }
    // The backend transparently creates a trip when the first item flips
    // pending → done. We need to wait for the PATCH to complete before
    // reloading trips, otherwise the new trip isn't visible yet.
    const wasPending = item.state === 'pending';
    const shouldRefreshTrips = wasPending && !this.activeTrip;
    this.sync.toggleState(item.client_id).then(
      () => {
        if (shouldRefreshTrips) {
          const hid = this.householdId;
          const listId = this.activeList?.id;
          if (hid && listId) this.loadActiveTrip(hid, listId);
        }
      },
      () => {
        this.toast.error('Could not update item.');
      },
    );
  }

  removeItem(item: LocalShoppingListItem): void {
    this.sync.removeItem(item.client_id);
  }

  retryItem(item: LocalShoppingListItem): void {
    this.sync.retry(item.client_id);
  }

  finishTrip(): void {
    const hid = this.householdId;
    const listId = this.activeList?.id;
    const tripId = this.activeTrip?.id;
    if (!hid || !listId || !tripId || this.finishingTrip) return;

    this.finishingTrip = true;
    this.shoppingService.completeTrip(hid, listId, tripId).subscribe({
      next: () => {
        this.activeTrip = null;
        this.finishingTrip = false;
        // Server resets all done → pending; reload to pick that up.
        this.loadListItems(hid, listId);
      },
      error: err => {
        this.log.error('Failed to complete trip', err, { tripId });
        this.finishingTrip = false;
        this.toast.error('Could not finish trip.');
      },
    });
  }

  // ── Derived view state ────────────────────────────────────────────────────

  get pendingItems(): LocalShoppingListItem[] {
    return this.items().filter(i => i.state === 'pending');
  }

  get doneItems(): LocalShoppingListItem[] {
    return this.items().filter(i => i.state === 'done');
  }

  get pendingCount(): number {
    return this.pendingItems.length;
  }

  get doneCount(): number {
    return this.doneItems.length;
  }

  get totalCount(): number {
    return this.items().length;
  }

  get householdId(): number | undefined {
    return this.context()?.household?.id ?? undefined;
  }

  // ── Icon helpers ──────────────────────────────────────────────────────────

  get iconVariant(): 'black' | 'white' {
    return this.themeService.theme() === 'light' ? 'black' : 'white';
  }

  /**
   * Icon color for an item card. Pending cards sit on a saturated teal in both
   * themes, so their icons are always white. Done cards recede onto a pale sage
   * surface in light theme, where black ink reads best (dark theme is dark, so
   * white). Everything else (autosuggest dropdown, trip picker) sits on the
   * page surface and follows the theme.
   */
  productIconUrl(productKindKey: string, state?: 'pending' | 'done'): string {
    let variant: 'black' | 'white';
    if (state === 'pending') {
      variant = 'white';
    } else if (state === 'done') {
      variant = this.themeService.theme() === 'light' ? 'black' : 'white';
    } else {
      variant = this.iconVariant;
    }
    return this.activeIconPack.iconUrl(productKindKey, variant);
  }

  private applyIconAspectClass(img: HTMLImageElement): void {
    img.classList.remove(
      'shopping-item-icon--landscape',
      'shopping-item-icon--portrait',
      'shopping-item-icon--square',
    );
    const { naturalWidth: w, naturalHeight: h } = img;
    if (!w || !h) return;
    if (w > h) img.classList.add('shopping-item-icon--landscape');
    else if (h > w) img.classList.add('shopping-item-icon--portrait');
    else img.classList.add('shopping-item-icon--square');
  }

  isIconLoading(clientId: string): boolean {
    return this.iconLoading[clientId] !== false && !this.iconFailed[clientId];
  }

  onIconLoad(clientId: string, img: HTMLImageElement): void {
    this.iconLoading[clientId] = false;
    this.iconFailed[clientId] = false;
    this.applyIconAspectClass(img);
  }

  onIconError(clientId: string, img: HTMLImageElement): void {
    this.iconLoading[clientId] = false;
    this.iconFailed[clientId] = true;
    img.style.display = 'none';
  }

  // ── Product-thumbnail icon helpers (suggestion panels) ────────────────────

  isProdIconLoading(productId: number): boolean {
    return this.prodIconLoading[productId] !== false && !this.prodIconFailed[productId];
  }

  onProdIconLoad(productId: number, img: HTMLImageElement): void {
    this.prodIconLoading[productId] = false;
    this.prodIconFailed[productId] = false;
    this.applyIconAspectClass(img);
  }

  onProdIconError(productId: number, img: HTMLImageElement): void {
    this.prodIconLoading[productId] = false;
    this.prodIconFailed[productId] = true;
    img.style.display = 'none';
  }
}


